import { useState, useEffect, useRef, useCallback } from 'react';
import { recordingsApi } from '../api';

const POLL_MS = 1500;
const START_TIMEOUT_MS = 30_000;      // camera connect should be near-instant on a LAN
const JOB_POLL_MS = 2000;
const JOB_TIMEOUT_MS = 10 * 60_000;   // safety net for a background upload/encode/email job
const JOB_TOAST_MS = 4000;            // how long the one-time "saved" confirmation stays visible

/**
 * Manages the kiosk's recording lifecycle for a single court.
 *
 * phase: 'idle' | 'starting' | 'recording' | 'error'
 *   - 'starting': start command sent, waiting for the agent to confirm the
 *     camera connected and FFmpeg is running (usually a couple seconds)
 *   - 'recording': FFmpeg is running on the onsite agent
 *
 * There is deliberately no blocking 'stopping'/'processing' phase. The
 * moment Stop is tapped, the stop command is queued and this hook returns
 * straight to 'idle' — the kiosk is immediately ready to record again. The
 * finalize/upload/encode/email work that follows happens entirely in the
 * background and is tracked as a `backgroundJob`, independent of `phase`,
 * so it can keep running (and be shown as a small non-blocking status)
 * across however many new recordings get started on this court while it
 * finishes up.
 */
export function useRecording(courtId) {
  const [phase, setPhase] = useState('idle');
  const [session, setSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);  // seconds
  const [error, setError] = useState(null);
  const [backgroundJobs, setBackgroundJobs] = useState([]);

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const pollRef = useRef(null);
  const jobPollsRef = useRef({}); // sessionId -> interval id
  const jobToastTimersRef = useRef({}); // sessionId -> timeout id

  const stopPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = null;
  }, []);

  // Polls a session's FULL detail until it reaches one of `untilStatuses`,
  // or times out. Used only for the (short) start phase.
  const pollUntil = useCallback((sessionId, untilStatuses, onSettle, timeoutMs) => {
    stopPolling();
    const deadline = Date.now() + timeoutMs;

    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        stopPolling();
        setError('Timed out waiting for the recorder — check that the onsite box is online');
        setPhase('error');
        return;
      }
      try {
        const latest = await recordingsApi.get(sessionId);
        setSession(latest);
        if (untilStatuses.includes(latest.status)) {
          stopPolling();
          onSettle(latest);
        }
      } catch {
        // Transient network hiccup talking to our own API — keep polling,
        // the timeout above is the real backstop.
      }
    }, POLL_MS);
  }, [stopPolling]);

  const updateJob = useCallback((id, patch) => {
    setBackgroundJobs(prev => prev.map(j => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  const dismissJob = useCallback((id) => {
    clearInterval(jobPollsRef.current[id]);
    delete jobPollsRef.current[id];
    clearTimeout(jobToastTimersRef.current[id]);
    delete jobToastTimersRef.current[id];
    setBackgroundJobs(prev => prev.filter(j => j.id !== id));
  }, []);

  // Starts tracking a just-stopped session as a background job: polls its
  // lightweight status until it reaches a terminal state (so a failure can
  // still be surfaced), but the confirmation itself is shown ONCE, as a
  // brief toast — not as a running progress indicator through upload/
  // encode/email. It auto-dismisses after JOB_TOAST_MS unless the job has
  // already failed by then, in which case it's left up for the user to see
  // and manually dismiss.
  const trackBackgroundJob = useCallback((stoppedSession) => {
    const id = stoppedSession.id;
    clearInterval(jobPollsRef.current[id]);
    clearTimeout(jobToastTimersRef.current[id]);

    setBackgroundJobs(prev => [
      ...prev.filter(j => j.id !== id),
      {
        id,
        status: stoppedSession.status,
        processing_stage: stoppedSession.processing_stage,
        player_email: stoppedSession.player_email,
        error_message: null,
        session: stoppedSession,
      },
    ]);

    jobToastTimersRef.current[id] = setTimeout(() => {
      delete jobToastTimersRef.current[id];
      setBackgroundJobs(prev => {
        const job = prev.find(j => j.id === id);
        if (!job || job.status === 'failed') return prev; // leave failures visible
        return prev.filter(j => j.id !== id);
      });
    }, JOB_TOAST_MS);

    const deadline = Date.now() + JOB_TIMEOUT_MS;
    jobPollsRef.current[id] = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(jobPollsRef.current[id]);
        delete jobPollsRef.current[id];
        updateJob(id, { status: 'failed', error_message: 'This is taking longer than expected — check the admin dashboard.' });
        return;
      }
      try {
        const latest = await recordingsApi.status(id);
        updateJob(id, {
          status: latest.status,
          processing_stage: latest.processing_stage,
          error_message: latest.error_message,
        });
        if (['completed', 'failed'].includes(latest.status)) {
          clearInterval(jobPollsRef.current[id]);
          delete jobPollsRef.current[id];
          if (latest.status === 'completed') {
            // Succeeded — nothing further to show; let the toast (if still
            // up) run out its own timer rather than lingering as a
            // permanent status pill.
            return;
          }
          try {
            const full = await recordingsApi.get(id);
            updateJob(id, { session: full });
          } catch {
            // Keep the lightweight data we already have — still enough
            // to show a sensible outcome.
          }
        }
      } catch {
        // Transient network hiccup — keep polling, the timeout above is
        // the backstop.
      }
    }, JOB_POLL_MS);
  }, [updateJob]);

  // On mount, recover an in-flight camera session for this court (covers a
  // kiosk page refresh mid-start or mid-recording — a session that's just
  // uploading/encoding/emailing in the background is intentionally NOT
  // restored here, since it never blocked the kiosk UI to begin with).
  useEffect(() => {
    if (!courtId) return;
    recordingsApi.getActive(courtId).then(data => {
      const active = data.active_session;
      if (!active) return;

      setSession(active);

      if (active.status === 'recording') {
        setPhase('recording');
        const started = new Date(active.started_at);
        startTimeRef.current = started;
        setElapsed(Math.floor((Date.now() - started.getTime()) / 1000));
      } else if (active.status === 'pending') {
        setPhase('starting');
        pollUntil(active.id, ['recording', 'failed'], (latest) => {
          setSession(latest);
          if (latest.status === 'recording') {
            setPhase('recording');
            startTimeRef.current = new Date(latest.started_at);
          } else {
            setError(latest.error_message || 'Recording failed to start');
            setPhase('error');
          }
        }, START_TIMEOUT_MS);
      }
    }).catch(() => {}); // Silently ignore — kiosk still usable
  }, [courtId]);

  // Elapsed timer, only while actually recording
  useEffect(() => {
    if (phase === 'recording') {
      if (!startTimeRef.current) startTimeRef.current = new Date();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000));
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  useEffect(() => {
    return () => {
      stopPolling();
      Object.values(jobPollsRef.current).forEach(clearInterval);
      Object.values(jobToastTimersRef.current).forEach(clearTimeout);
    };
  }, [stopPolling]);

  const startRecording = useCallback(async (playerEmail, extraEmails = []) => {
    if (!courtId) return;
    setError(null);
    setPhase('starting');
    setElapsed(0);
    try {
      const newSession = await recordingsApi.start(courtId, playerEmail, extraEmails);
      setSession(newSession);
      pollUntil(newSession.id, ['recording', 'failed'], (latest) => {
        setSession(latest);
        if (latest.status === 'recording') {
          setPhase('recording');
          startTimeRef.current = new Date(latest.started_at);
        } else {
          setError(latest.error_message || 'Recording failed to start — is the onsite box online?');
          setPhase('error');
        }
      }, START_TIMEOUT_MS);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to start recording';
      setError(msg);
      setPhase('error');
    }
  }, [courtId, pollUntil]);

  // Queues the stop command, then returns straight to 'idle' — it does NOT
  // wait for finalize/upload/encode/email. That work is tracked as a
  // background job instead (see trackBackgroundJob), so the kiosk is ready
  // for the next player immediately.
  const stopRecording = useCallback(async () => {
    if (!session) return;
    const stoppingSession = session;
    try {
      const stopped = await recordingsApi.stop(stoppingSession.id);
      trackBackgroundJob(stopped);
      stopPolling();
      setPhase('idle');
      setSession(null);
      setElapsed(0);
      startTimeRef.current = null;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to stop recording';
      setError(msg);
      setPhase('error');
    }
  }, [session, trackBackgroundJob, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setPhase('idle');
    setSession(null);
    setElapsed(0);
    setError(null);
    startTimeRef.current = null;
  }, [stopPolling]);

  const formatElapsed = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return {
    phase,
    session,
    elapsed,
    elapsedDisplay: formatElapsed(elapsed),
    error,
    startRecording,
    stopRecording,
    reset,
    backgroundJobs,
    dismissJob,
  };
}
